import { useState, useEffect } from 'react';
import { useWorkspace } from '@/contexts/WorkspaceContext';
import { useAuth } from '@/hooks/useAuth';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { useToast } from '@/hooks/use-toast';
import { Mail, UserPlus, Loader2, CheckCircle2, Building2, Plus, RefreshCw } from 'lucide-react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { handleInviteError, isRetryableError, type InviteErrorUI } from '@/utils/inviteErrorHandler';
import { useUpdateEntitlements } from '@/hooks/useEntitlements';

interface InviteUserModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

interface ModuleAccess {
  module_id: string;
  enabled: boolean;
  permissions: {
    view: boolean;
    edit: boolean;
    create: boolean;
    delete: boolean;
    admin: boolean;
  };
}

// Module ID mapping: string IDs to actual module names/descriptions
// These will be matched against workspace.enabled_modules JSONB array
const AVAILABLE_MODULES = [
  { id: 'bill_collection', name: 'Bill Collection', description: 'Manage bill collection and queue' },
  { id: 'bill_processing', name: 'Bill Processing', description: 'Process and parse bills' },
  { id: 'onboarding', name: 'Onboarding', description: 'Customer and partner onboarding' },
  { id: 'ai_parser', name: 'AI Parser', description: 'AI-powered bill parsing' },
  { id: 'ai_agents', name: 'AI Agents', description: 'AI agent management' },
  { id: 'automation', name: 'Automation', description: 'Workflow automation' },
  { id: 'reporting', name: 'Reporting', description: 'Reports and analytics' },
  { id: 'analytics', name: 'Analytics', description: 'Advanced analytics' },
  { id: 'integrations', name: 'Integrations', description: 'Third-party integrations' },
  { id: 'api', name: 'API Access', description: 'API management' },
  { id: 'tum', name: 'Tenant Utility Manager', description: 'TUM module access' },
  { id: 'workspace_admin', name: 'Workspace Admin', description: 'Workspace administration' },
];

export function InviteUserModal({ open, onOpenChange }: InviteUserModalProps) {
  const { currentWorkspace, isOwner } = useWorkspace();
  const { user, session, refreshSession } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const [email, setEmail] = useState('');
  const [role, setRole] = useState<'admin' | 'manager' | 'analyst' | 'viewer'>('viewer');
  const [selectedModules, setSelectedModules] = useState<Record<string, ModuleAccess>>({});
  const [customMessage, setCustomMessage] = useState('');
  const [expiresInDays, setExpiresInDays] = useState(7);
  const [workspaceOption, setWorkspaceOption] = useState<'existing' | 'new'>('existing');
  const [selectedWorkspaceId, setSelectedWorkspaceId] = useState<string>('');
  const [newWorkspaceName, setNewWorkspaceName] = useState('');
  const [newWorkspacePlan, setNewWorkspacePlan] = useState<string>('');
  const [includeTrialPlan, setIncludeTrialPlan] = useState<boolean>(true);
  const [resendInviteMeta, setResendInviteMeta] = useState<{ invite_id?: string } | null>(null);
  const [errorUI, setErrorUI] = useState<InviteErrorUI | null>(null);

  // Initialize selected workspace to current workspace when dialog opens
  useEffect(() => {
    if (open && workspaceOption === 'existing' && !selectedWorkspaceId && currentWorkspace?.id) {
      setSelectedWorkspaceId(currentWorkspace.id);
    }
  }, [open, workspaceOption, currentWorkspace?.id, selectedWorkspaceId]);

  // Initialize selected workspace to current workspace when dialog opens
  useEffect(() => {
    if (open && workspaceOption === 'existing' && !selectedWorkspaceId && currentWorkspace?.id) {
      setSelectedWorkspaceId(currentWorkspace.id);
    }
  }, [open, workspaceOption, currentWorkspace?.id]);

  // Fetch available workspaces for selection
  const { data: availableWorkspaces = [] } = useQuery({
    queryKey: ['available-workspaces', user?.id],
    queryFn: async () => {
      const { data, error } = await supabase.functions.invoke('workspaces-list', {
        method: 'GET',
      });
      if (error) throw error;
      return data?.workspaces || [];
    },
    enabled: !!user?.id && open && workspaceOption === 'existing',
  });

  // Fetch available plans for new workspace creation
  const { data: availablePlans = [] } = useQuery({
    queryKey: ['available-plans'],
    queryFn: async () => {
      const { data, error } = await supabase.functions.invoke('workspace-plans', {
        body: { action: 'list' }
      });
      if (error) throw error;
      return data?.plans || [];
    },
    enabled: open && workspaceOption === 'new',
  });

  // Fetch workspace module access configuration and plan info
  // Use selected workspace if available, otherwise fall back to current workspace
  const targetWorkspaceId = workspaceOption === 'existing' 
    ? (selectedWorkspaceId || currentWorkspace?.id) 
    : null;
  const { data: workspaceModulesData } = useQuery({
    queryKey: ['workspace-modules', targetWorkspaceId],
    queryFn: async () => {
      if (!targetWorkspaceId) return null;
      
      const { data, error } = await supabase.functions.invoke('entitlements-get', {
        body: { scope: 'workspace', id: targetWorkspaceId }
      });

      if (error) throw error;
      return data || {};
    },
    enabled: !!targetWorkspaceId && open,
  });

  // Also fetch workspace details to get enabled_modules JSONB array
  const { data: workspaceDetails } = useQuery({
    queryKey: ['workspace-details', targetWorkspaceId],
    queryFn: async () => {
      if (!targetWorkspaceId) return null;
      
      const { data, error } = await supabase
        .from('workspaces')
        .select('id, enabled_modules')
        .eq('id', targetWorkspaceId)
        .single();

      if (error) throw error;
      return data;
    },
    enabled: !!targetWorkspaceId && open,
  });

  const workspaceModules = workspaceModulesData?.modules || [];
  const currentPlan = workspaceModulesData?.plan || null;
  // Use workspaceDetails for enabled_modules check, fallback to currentWorkspace
  const targetWorkspace = workspaceDetails || currentWorkspace;

  // Initialize selected modules based on role and plan availability
  const initializeModules = (selectedRole: string) => {
    const defaultModules: Record<string, ModuleAccess> = {};
    
    AVAILABLE_MODULES.forEach(module => {
      // Check module availability - match by module_id (UUID), name, or module.id (string)
      // The entitlements-get returns modules with UUID module_id and string name (e.g., name: "tum")
      const workspaceModule = workspaceModules?.find((m: any) => {
        // Match by module_id if it's a string (legacy) or exact UUID match
        if (m.module_id === module.id) return true;
        // Match by name (for UUID-based modules, name is "tum", "bill_collection", etc.)
        // This is the primary matching method for UUID-based modules
        if (m.name === module.id || m.name === module.name) return true;
        // Also check if module_id is a UUID (36 chars) and match by name
        if (typeof m.module_id === 'string' && m.module_id.length === 36 && m.name === module.id) return true;
        return false;
      });
      
      // Also check workspace.enabled_modules JSONB array (contains string IDs like "tum")
      // This is critical for TUM since it's in enabled_modules: ["tum", ...]
      const isInEnabledModules = targetWorkspace?.enabled_modules?.includes(module.id) || false;
      
      // Module is available if:
      // 1. User is owner (owners can enable any module at workspace level)
      // 2. Enabled in workspace modules from entitlements-get (workspaceModule?.enabled)
      // 3. In workspace.enabled_modules JSONB array (isInEnabledModules)
      // 4. Source indicates it's available (plan, workspace_enabled, owner)
      const isInPlan = isOwner || // Owners can enable any module
                      workspaceModule?.enabled || 
                      isInEnabledModules || 
                      workspaceModule?.source === 'plan' || 
                      workspaceModule?.source === 'workspace_enabled' ||
                      workspaceModule?.source === 'owner';
      const canAssign = isInPlan && (selectedRole === 'admin' || selectedRole === 'manager');
      
      defaultModules[module.id] = {
        module_id: module.id,
        enabled: canAssign,
        permissions: {
          view: true,
          edit: selectedRole === 'admin' || selectedRole === 'manager',
          create: selectedRole === 'admin',
          delete: selectedRole === 'admin',
          admin: selectedRole === 'admin',
        },
      };
    });
    
    setSelectedModules(defaultModules);
  };

  const handleRoleChange = (newRole: string) => {
    setRole(newRole as any);
    initializeModules(newRole);
  };

  const toggleModule = (moduleId: string) => {
    setSelectedModules(prev => ({
      ...prev,
      [moduleId]: {
        ...prev[moduleId],
        enabled: !prev[moduleId]?.enabled,
      },
    }));
  };

  const updateModulePermission = (moduleId: string, permission: string, value: boolean) => {
    setSelectedModules(prev => ({
      ...prev,
      [moduleId]: {
        ...prev[moduleId],
        permissions: {
          ...prev[moduleId]?.permissions,
          [permission]: value,
        },
      },
    }));
  };

  // Create workspace mutation
  const createWorkspaceMutation = useMutation({
    mutationFn: async () => {
      if (!newWorkspaceName.trim() || (!newWorkspacePlan && !includeTrialPlan) || !user?.id) {
        throw new Error('Workspace name and plan are required');
      }

      const enabledModules = Object.values(selectedModules)
        .filter(m => m.enabled)
        .map(m => m.module_id);

      // Use trial plan if includeTrialPlan is true, otherwise use selected plan
      const planCode = includeTrialPlan ? 'trial' : newWorkspacePlan;

      const { data, error } = await supabase.functions.invoke('workspaces-manage', {
        body: {
          action: 'create',
          name: newWorkspaceName.trim(),
          plan_code: planCode,
          module_overrides: enabledModules.map(moduleId => ({
            module_id: moduleId,
            enabled: true,
          })),
        },
      });

      if (error) throw error;
      return data;
    },
  });

  const inviteMutation = useMutation({
    mutationFn: async () => {
      let finalWorkspaceId = selectedWorkspaceId;

      // If creating new workspace, create it first
      if (workspaceOption === 'new') {
        const workspaceResult = await createWorkspaceMutation.mutateAsync();
        finalWorkspaceId = workspaceResult?.workspace?.id;
        if (!finalWorkspaceId) {
          throw new Error('Failed to create workspace');
        }
      }

      if (!finalWorkspaceId || !user?.id) {
        throw new Error('Workspace or user not found');
      }

      const enabledModules = Object.values(selectedModules)
        .filter(m => m.enabled)
        .map(m => ({
          module_id: m.module_id,
          permissions: m.permissions,
        }));

      // Get fresh session token - this is critical for authentication
      const { data: sessionData, error: sessionError } = await supabase.auth.getSession();
      
      if (sessionError || !sessionData?.session?.access_token) {
        // Try to refresh the session
        const refreshResult = await refreshSession();
        if (!refreshResult.success) {
          throw new Error('No active session. Please log in again.');
        }
        // Get session again after refresh
        const { data: refreshedSession } = await supabase.auth.getSession();
        if (!refreshedSession?.session?.access_token) {
          throw new Error('Failed to refresh session. Please log in again.');
        }
      }

      // Get the access token for explicit header passing
      const finalSession = sessionData?.session || (await supabase.auth.getSession()).data?.session;
      const accessToken = finalSession?.access_token;
      
      if (!accessToken) {
        throw new Error('No active session token available. Please log in again.');
      }

      // Prepare auth header - we'll pass this explicitly to ensure it works
      const authHeader = `Bearer ${accessToken}`;

      // If owner and modules are selected, enable them at workspace level first
      if (isOwner && enabledModules.length > 0 && finalWorkspaceId) {
        try {
          // Enable modules at workspace level - use workspace-modules function instead
          // which supports bulk updates
          const moduleOverrides = enabledModules.map(m => ({
            module_id: m.module_id,
            enabled: true,
          }));

          const { error: modulesError } = await supabase.functions.invoke('workspace-modules', {
            body: {
              action: 'update_overrides',
              workspace_id: finalWorkspaceId,
              module_overrides: moduleOverrides,
            },
            headers: {
              Authorization: authHeader,
            },
          });

          if (modulesError) {
            console.error('[InviteUserModal] Error enabling modules at workspace level:', modulesError);
            // Continue with invitation even if module enablement fails
          }
        } catch (error) {
          console.error('[InviteUserModal] Error enabling modules at workspace level:', error);
          // Continue with invitation even if module enablement fails
        }
      }

      // Pass Authorization header explicitly - same pattern as workspace-modules which works
      const { data, error } = await supabase.functions.invoke('workspace-invite-user', {
        body: {
          workspace_id: finalWorkspaceId,
          email: email.trim(),
          role,
          invited_by: user.id,
          module_access: enabledModules,
          expires_in_days: expiresInDays,
          custom_message: customMessage || undefined,
        },
        headers: {
          'Authorization': authHeader,
        },
      });

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      toast({
        title: workspaceOption === 'new' ? 'Workspace Created & Invitation Sent' : 'Invitation Sent',
        description: workspaceOption === 'new' 
          ? `New workspace created and invitation sent to ${email}`
          : `Invitation email has been sent to ${email}`,
      });
      queryClient.invalidateQueries({ queryKey: ['workspace-invitations'] });
      queryClient.invalidateQueries({ queryKey: ['workspace-members'] });
      queryClient.invalidateQueries({ queryKey: ['workspaces'] });
      onOpenChange(false);
      setEmail('');
      setCustomMessage('');
      setSelectedModules({});
      setNewWorkspaceName('');
      setNewWorkspacePlan('');
      setWorkspaceOption('existing');
    },
    onError: (error: any) => {
      // Use centralized error handler
      const ui = handleInviteError(error);
      setErrorUI(ui);

      // Show toast with actionable message
      toast({
        title: ui.title,
        description: ui.description,
        variant: ui.variant === 'destructive' ? 'destructive' : 'default',
      });

      // Set up resend action if applicable
      if (ui.action === 'RESEND' && ui.meta?.invite_id) {
        setResendInviteMeta({ invite_id: ui.meta.invite_id });
      } else {
        setResendInviteMeta(null);
      }
    },
  });

  // Resend invitation mutation
  const resendMutation = useMutation({
    mutationFn: async () => {
      if (!resendInviteMeta?.invite_id) {
        throw new Error('No invitation ID available');
      }

      const { data: sessionData } = await supabase.auth.getSession();
      const accessToken = sessionData?.session?.access_token;
      
      if (!accessToken) {
        throw new Error('No active session');
      }

      const response = await supabase.functions.invoke('workspace-resend-invitation', {
        body: {
          invitation_id: resendInviteMeta.invite_id,
        },
        headers: {
          'Authorization': `Bearer ${accessToken}`,
        },
      });

      if (response.error) {
        const errorData = response.error;
        if (errorData.code || (errorData as any)?.error?.code) {
          throw errorData;
        }
        throw {
          code: 'INTERNAL_ERROR',
          message: errorData.message || 'An unexpected error occurred',
        };
      }

      if (response.data?.success === false && response.data.error) {
        throw response.data.error;
      }

      return response.data;
    },
    onSuccess: () => {
      toast({
        title: 'Invitation resent',
        description: 'The invitation has been resent successfully.',
        variant: 'default',
      });
      queryClient.invalidateQueries({ queryKey: ['workspace-invitations'] });
      setResendInviteMeta(null);
      setErrorUI(null);
      onOpenChange(false);
    },
    onError: (error: any) => {
      const ui = handleInviteError(error);
      toast({
        title: ui.title,
        description: ui.description,
        variant: ui.variant === 'destructive' ? 'destructive' : 'default',
      });
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim()) {
      toast({
        title: 'Error',
        description: 'Please enter an email address',
        variant: 'destructive',
      });
      return;
    }
    inviteMutation.mutate();
  };

  const handleResend = () => {
    if (resendInviteMeta?.invite_id) {
      resendMutation.mutate();
    }
  };

  // Initialize modules when dialog opens or workspace modules change
  useEffect(() => {
    if (open && (workspaceModules.length > 0 || targetWorkspace)) {
      initializeModules(role);
    }
  }, [open, workspaceModules, role, targetWorkspaceId, targetWorkspace]);

  // Update selected workspace ID when current workspace changes
  useEffect(() => {
    if (currentWorkspace?.id && workspaceOption === 'existing') {
      setSelectedWorkspaceId(currentWorkspace.id);
    }
  }, [currentWorkspace?.id, workspaceOption]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <UserPlus className="h-5 w-5" />
            Invite User to Workspace
          </DialogTitle>
          <DialogDescription>
            Send an invitation to join {currentWorkspace?.name} with customized module access
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Workspace Selection */}
          <div className="space-y-4 border-b pb-4">
            <Label className="text-base font-semibold">Workspace Selection</Label>
            <RadioGroup value={workspaceOption} onValueChange={(value) => setWorkspaceOption(value as 'existing' | 'new')}>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="existing" id="existing" />
                <Label htmlFor="existing" className="font-normal cursor-pointer">Invite to Existing Workspace</Label>
              </div>
              {workspaceOption === 'existing' && (
                <div className="ml-6 mt-2">
                  <Select value={selectedWorkspaceId} onValueChange={setSelectedWorkspaceId}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select workspace" />
                    </SelectTrigger>
                    <SelectContent>
                      {availableWorkspaces.map((ws: any) => (
                        <SelectItem key={ws.id} value={ws.id}>
                          {ws.name} {ws.plan && `(${ws.plan.name})`}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="new" id="new" />
                <Label htmlFor="new" className="font-normal cursor-pointer">Create New Workspace</Label>
              </div>
              {workspaceOption === 'new' && (
                <div className="ml-6 mt-2 space-y-3">
                  <div className="space-y-2">
                    <Label htmlFor="new-workspace-name">Workspace Name *</Label>
                    <Input
                      id="new-workspace-name"
                      placeholder="Enter workspace name"
                      value={newWorkspaceName}
                      onChange={(e) => setNewWorkspaceName(e.target.value)}
                      required={workspaceOption === 'new'}
                    />
                  </div>
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <Label htmlFor="new-workspace-plan">Plan *</Label>
                      <div className="flex items-center space-x-2">
                        <Checkbox
                          id="include-trial"
                          checked={includeTrialPlan}
                          onCheckedChange={(checked) => {
                            setIncludeTrialPlan(checked as boolean);
                            if (checked) {
                              setNewWorkspacePlan('trial');
                            }
                          }}
                        />
                        <Label htmlFor="include-trial" className="text-sm font-normal cursor-pointer">
                          Use Trial Plan (Default)
                        </Label>
                      </div>
                    </div>
                    <Select 
                      value={includeTrialPlan ? 'trial' : newWorkspacePlan} 
                      onValueChange={(value) => {
                        if (value === 'trial') {
                          setIncludeTrialPlan(true);
                          setNewWorkspacePlan('trial');
                        } else {
                          setIncludeTrialPlan(false);
                          setNewWorkspacePlan(value);
                        }
                      }}
                      required={workspaceOption === 'new'}
                    >
                      <SelectTrigger id="new-workspace-plan">
                        <SelectValue placeholder="Select plan" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="trial">
                          Trial Management Plan (Default)
                        </SelectItem>
                        {availablePlans.map((plan: any) => (
                          <SelectItem key={plan.code} value={plan.code}>
                            {plan.name} {plan.modules && `(${plan.modules.length} modules)`}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {includeTrialPlan && (
                      <p className="text-xs text-muted-foreground">
                        Trial plan will be assigned by default. You can select modules below.
                      </p>
                    )}
                  </div>
                </div>
              )}
            </RadioGroup>
          </div>

          {/* Basic Information */}
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="email">Email Address *</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="user@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="role">Role *</Label>
                <Select value={role} onValueChange={handleRoleChange}>
                  <SelectTrigger id="role">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="admin">Admin</SelectItem>
                    <SelectItem value="manager">Manager</SelectItem>
                    <SelectItem value="analyst">Analyst</SelectItem>
                    <SelectItem value="viewer">Viewer</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="expires">Invitation Expires In (Days)</Label>
              <Input
                id="expires"
                type="number"
                min="1"
                max="30"
                value={expiresInDays}
                onChange={(e) => setExpiresInDays(parseInt(e.target.value) || 7)}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="message">Custom Message (Optional)</Label>
              <Textarea
                id="message"
                placeholder="Add a personalized message to the invitation email..."
                value={customMessage}
                onChange={(e) => setCustomMessage(e.target.value)}
                rows={3}
              />
            </div>
          </div>

          {/* Module Access Configuration */}
          <div className="space-y-4 border-t pt-4">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="font-semibold">Module Access Configuration</h3>
                <p className="text-sm text-muted-foreground">
                  Configure which modules this user can access in this workspace
                  {currentPlan && (
                    <span className="ml-2">
                      • Plan: <Badge variant="secondary" className="ml-1">{currentPlan.name || currentPlan.code}</Badge>
                    </span>
                  )}
                </p>
              </div>
              <Badge variant="outline">
                {Object.values(selectedModules).filter(m => m.enabled).length} modules enabled
              </Badge>
            </div>

            <div className="grid grid-cols-1 gap-3 max-h-[400px] overflow-y-auto">
              {AVAILABLE_MODULES.map(module => {
                const moduleAccess = selectedModules[module.id];
                const isEnabled = moduleAccess?.enabled || false;
                const canEdit = role === 'admin' || role === 'manager';
                
                // Check module availability - match by module_id (UUID), name, or module.id (string)
                // The entitlements-get returns modules with UUID module_id and string name
                const workspaceModule = workspaceModules?.find((m: any) => {
                  // Match by module_id if it's a string (legacy) or UUID matches
                  if (m.module_id === module.id) return true;
                  // Match by name (for UUID-based modules, name is "tum", "bill_collection", etc.)
                  if (m.name === module.id || m.name === module.name) return true;
                  // Also check if module_id is a UUID and we need to match by name
                  if (typeof m.module_id === 'string' && m.module_id.length === 36 && m.name === module.id) return true;
                  return false;
                });
                
                // Also check workspace.enabled_modules JSONB array (contains string IDs like "tum")
                // This is CRITICAL for TUM since it's in enabled_modules: ["tum", ...]
                const isInEnabledModules = targetWorkspace?.enabled_modules?.includes(module.id) || false;
                
                // Debug logging for TUM module
                if (module.id === 'tum') {
                  console.log('[InviteUserModal] TUM Module Check:', {
                    moduleId: module.id,
                    workspaceModule: workspaceModule,
                    workspaceModuleEnabled: workspaceModule?.enabled,
                    workspaceModuleSource: workspaceModule?.source,
                    workspaceModuleName: workspaceModule?.name,
                    isInEnabledModules,
                    targetWorkspaceEnabledModules: targetWorkspace?.enabled_modules,
                    targetWorkspaceId,
                    workspaceModulesCount: workspaceModules.length,
                    workspaceModules: workspaceModules.map((m: any) => ({ id: m.module_id, name: m.name, enabled: m.enabled })),
                  });
                }
                
                // Module is available if:
                // 1. User is owner (owners can enable any module at workspace level)
                // 2. Enabled in workspace modules from entitlements-get (workspaceModule?.enabled)
                // 3. In workspace.enabled_modules JSONB array (isInEnabledModules) - THIS IS KEY FOR TUM
                // 4. Source indicates it's available (plan, workspace_enabled, owner)
                const isInPlan = isOwner || // Owners can enable any module
                                workspaceModule?.enabled || 
                                isInEnabledModules || 
                                workspaceModule?.source === 'plan' || 
                                workspaceModule?.source === 'workspace_enabled' ||
                                workspaceModule?.source === 'owner';
                const isPlanModule = workspaceModule?.source === 'plan' || workspaceModule?.source === 'workspace_enabled';
                const isOverride = workspaceModule?.source === 'workspace_override' || workspaceModule?.source === 'override';
                const isOwnerModule = workspaceModule?.source === 'owner';

                return (
                  <div
                    key={module.id}
                    className={`p-4 border rounded-lg ${
                      isEnabled ? 'border-primary bg-primary/5' : 'border-border'
                    } ${!isInPlan && !isOwner ? 'opacity-60' : ''}`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-3">
                          <Checkbox
                            checked={isEnabled}
                            onCheckedChange={() => toggleModule(module.id)}
                            disabled={(!canEdit && role !== 'admin') || (!isInPlan && !isOwner)}
                          />
                          <div className="flex-1">
                            <div className="flex items-center gap-2">
                              <div className="font-medium">{module.name}</div>
                              {isPlanModule && (
                                <Badge variant="outline" className="text-xs">In Plan</Badge>
                              )}
                              {isOverride && (
                                <Badge variant="secondary" className="text-xs">Override</Badge>
                              )}
                              {isOwnerModule && (
                                <Badge variant="default" className="text-xs">Owner</Badge>
                              )}
                              {!isInPlan && !isOwner && (
                                <Badge variant="destructive" className="text-xs">Not Available</Badge>
                              )}
                              {isOwner && !isInPlan && (
                                <Badge variant="secondary" className="text-xs">Owner Override</Badge>
                              )}
                            </div>
                            <div className="text-sm text-muted-foreground">{module.description}</div>
                            {!isInPlan && !isOwner && (
                              <div className="text-xs text-muted-foreground mt-1">
                                This module is not included in your current plan. Contact support to enable it.
                              </div>
                            )}
                            {isOwner && !isInPlan && (
                              <div className="text-xs text-blue-600 dark:text-blue-400 mt-1">
                                As owner, you can enable this module at workspace level.
                              </div>
                            )}
                          </div>
                        </div>

                        {isEnabled && canEdit && (
                          <div className="mt-3 ml-7 space-y-2">
                            <div className="flex flex-wrap gap-4">
                              <label className="flex items-center gap-2 text-sm">
                                <Checkbox
                                  checked={moduleAccess?.permissions.view || false}
                                  onCheckedChange={(checked) =>
                                    updateModulePermission(module.id, 'view', checked as boolean)
                                  }
                                />
                                View
                              </label>
                              <label className="flex items-center gap-2 text-sm">
                                <Checkbox
                                  checked={moduleAccess?.permissions.edit || false}
                                  onCheckedChange={(checked) =>
                                    updateModulePermission(module.id, 'edit', checked as boolean)
                                  }
                                />
                                Edit
                              </label>
                              <label className="flex items-center gap-2 text-sm">
                                <Checkbox
                                  checked={moduleAccess?.permissions.create || false}
                                  onCheckedChange={(checked) =>
                                    updateModulePermission(module.id, 'create', checked as boolean)
                                  }
                                />
                                Create
                              </label>
                              <label className="flex items-center gap-2 text-sm">
                                <Checkbox
                                  checked={moduleAccess?.permissions.delete || false}
                                  onCheckedChange={(checked) =>
                                    updateModulePermission(module.id, 'delete', checked as boolean)
                                  }
                                />
                                Delete
                              </label>
                              {role === 'admin' && (
                                <label className="flex items-center gap-2 text-sm">
                                  <Checkbox
                                    checked={moduleAccess?.permissions.admin || false}
                                    onCheckedChange={(checked) =>
                                      updateModulePermission(module.id, 'admin', checked as boolean)
                                    }
                                  />
                                  Admin
                                </label>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          <DialogFooter className="flex flex-col sm:flex-row gap-2">
            {resendInviteMeta && errorUI?.action === 'RESEND' && (
              <Button
                type="button"
                variant="outline"
                onClick={handleResend}
                disabled={resendMutation.isPending || inviteMutation.isPending}
                className="sm:mr-auto"
              >
                {resendMutation.isPending ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Resending...
                  </>
                ) : (
                  <>
                    <RefreshCw className="mr-2 h-4 w-4" />
                    {errorUI.actionLabel || 'Resend Invitation'}
                  </>
                )}
              </Button>
            )}
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                onOpenChange(false);
                setResendInviteMeta(null);
                setErrorUI(null);
              }}
              disabled={inviteMutation.isPending || resendMutation.isPending}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={inviteMutation.isPending || resendMutation.isPending}>
              {inviteMutation.isPending ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Sending...
                </>
              ) : (
                <>
                  <Mail className="mr-2 h-4 w-4" />
                  Send Invitation
                </>
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

